import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { authGet, authPut, BACKEND_URL, API, getAuthHeaders, hasToken } from "@/lib/api";
import {
  Plus,
  MapPin,
  User,
  Home,
  Users as UsersIcon,
  ClipboardList,
  MessageCircle,
  Trophy,
  Camera,
  Clock,
  ArrowRight,
  Music,
  Image as ImageIcon,
  Wand2,
  Sparkles,
  Activity,
  ChevronRight,
  Check,
  X,
  Edit,
  ChevronDown,
  Share2,
  MessageSquare,
  Mail,
  Copy,
  ArrowLeft,
  UtensilsCrossed,
  Download,
  HelpCircle
} from "lucide-react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { toast } from "sonner";
import { format, parseISO, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from "date-fns";
import EventRecapView from "@/components/EventRecapView";
import { de } from "date-fns/locale";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Feature Imports
import EventTimeline from "@/components/EventTimeline";
import EventWishlist from "@/components/EventWishlist";
import EventDresscode from "@/components/EventDresscode";
import SeatingPlan from "@/components/SeatingPlan";
import DishVoting from "@/components/DishVoting";
import GuestViewFloatingButton from "@/components/GuestViewFloatingButton";
import EventGalleryNew from "@/components/EventGalleryNew";
import GuestManagement from "@/components/GuestManagement";
import EventTaskList from "@/components/EventTaskList";

const THEME = {
  fontSerif: "'Cormorant Garamond', serif",
  fontSans: "'Plus Jakarta Sans', sans-serif"
};

const EVENT_TYPE_LABELS = {
  // English Defaults
  wedding: { label: "Hochzeit", emoji: "💍", image: "/images/headers/wedding.png" },
  birthday: { label: "Geburtstag", emoji: "🎂", image: "/images/headers/birthday_new.png" },
  party: { label: "Party", emoji: "🎉", image: "/images/headers/party_new.png" },
  bachelorette: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  graduation: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  anniversary: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  babyshower: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  baby_shower: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  baby: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  corporate: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  christmas: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  newyear: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  new_year: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  easter: { label: "Ostern", emoji: "🐰", image: "/images/headers/easter.png" },
  valentine: { label: "Valentinstag", emoji: "💖", image: "/images/headers/valentine.png" },

  // German & Aliases
  hochzeit: { label: "Hochzeit", emoji: "💍", image: "/images/headers/wedding.png" },
  geburtstag: { label: "Geburtstag", emoji: "🎂", image: "/images/headers/birthday_new.png" },
  feier: { label: "Party", emoji: "🎉", image: "/images/headers/party_new.png" },
  jga: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  junggesellenabschied: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  abschluss: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  abschlussfeier: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  jubilaeum: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  jubiläum: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  babyparty: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  taufe: { label: "Taufe", emoji: "✝️", image: "/images/headers/baby-shower.png" },
  gender_reveal: { label: "Gender Reveal", emoji: "👶", image: "/images/headers/baby-shower.png" },
  firma: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  firmenevent: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  business: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  weihnachten: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  weihnachtsfeier: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  silvester: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  ostern: { label: "Ostern", emoji: "🐰", image: "/images/headers/easter.png" },
  valentinstag: { label: "Valentinstag", emoji: "💖", image: "/images/headers/valentine.png" },

  other: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" },
  sonstiges: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" },
  individuell: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" }
};

const safelyFormatDate = (dateString, formatStr) => {
  try {
    if (!dateString) return "";
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return "";
    return format(date, formatStr, { locale: de });
  } catch (e) {
    return "";
  }
};

const GlobalBackgroundComponent = ({ heroImage, event, setHeroImage }) => (
  <div className="fixed inset-0 z-0 overflow-hidden bg-white">
    {heroImage && (
      <img
        src={heroImage}
        className="w-full h-full object-cover blur-[20px] scale-110 opacity-[0.12] grayscale"
        alt=""
        onError={(e) => {
          const typeInfo = EVENT_TYPE_LABELS[event?.event_type?.toLowerCase()] || EVENT_TYPE_LABELS.other;
          if (e.target.src !== typeInfo.image) {
            e.target.src = typeInfo.image;
            if (setHeroImage) setHeroImage(typeInfo.image);
          } else if (e.target.src !== "/images/headers/other.png") {
            e.target.src = "/images/headers/other.png";
          }
        }}
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-tr from-white via-white/40 to-brand-accent/5" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--brand-accent-hsl)_0.05,transparent_0.2)]" />
  </div>
);

// Alias User icon manually to avoid import issues
const ProfileIcon = User;

const ToggleSwitch = ({ checked, onCheckedChange, color = "emerald" }) => {
  const bgColors = {
    emerald: 'bg-emerald-500',
    pink: 'bg-pink-500',
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500',
  };
  const activeClass = bgColors[color] || 'bg-emerald-500';

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onCheckedChange && onCheckedChange(!checked); }}
      className={`w-12 h-7 rounded-full transition-colors p-1 relative ${checked ? activeClass : 'bg-gray-200'}`}
    >
      <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 absolute top-1 left-1 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
};

const SectionCard = ({ title, icon: Icon, color, children }) => {
  if (!Icon) return null; // Safety check
  const colorClasses = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-600' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
  }[color] || { bg: 'bg-gray-50', text: 'text-gray-600' };

  return (
    <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl ${colorClasses.bg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${colorClasses.text}`} />
          </div>
          <h3 className="text-lg font-black text-slate-900">{title}</h3>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300" />
      </div>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  );
};

// Updated FeatureRow to be clickable and show chevron
const FeatureRow = ({ icon: Icon, label, subLabel, checked, onChange, onClick, action, color = "emerald" }) => (
  <div
    className={`flex items-center justify-between p-2 -mx-2 rounded-xl transition-colors ${onClick ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center gap-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color === 'emerald' ? 'bg-emerald-50 text-emerald-600' : color === 'pink' ? 'bg-pink-50 text-pink-600' : 'bg-blue-50 text-blue-600'}`}>
        {Icon && <Icon className="w-5 h-5" />}
      </div>
      <div>
        <p className="font-bold text-slate-900 text-sm">{label}</p>
        <div className="flex items-center gap-2">
          {subLabel && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{subLabel}</p>}
          {onClick && <ChevronRight className="w-3 h-3 text-slate-300" />}
        </div>
      </div>
    </div>
    <div onClick={(e) => e.stopPropagation()}>
      {action ? action : <ToggleSwitch checked={checked} onCheckedChange={onChange} color={color} />}
    </div>
  </div>
);

// Detail View Wrapper for Submodules
const FeatureDetailView = ({ title, onBack, children }) => (
  <div className="min-h-screen bg-slate-50 relative pb-20">
    {/* Header */}
    <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-100 px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-900" />
        </button>
        <div>
          <h2 className="text-xl font-black text-slate-900 leading-none">{title}</h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Einstellungen</p>
        </div>
      </div>
    </div>
    {/* Content */}
    <div className="p-0 md:p-6">
      {children}
    </div>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) {
    console.error("Event Control Center Crash:", error, errorInfo);
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 m-4 bg-red-50 border border-red-200 rounded-xl text-red-900 overflow-auto font-mono text-sm">
          <h2 className="text-xl font-bold mb-4">Control Center Error</h2>
          <p className="mb-2 font-bold">{this.state.error?.toString()}</p>
          <details className="whitespace-pre-wrap"><summary className="cursor-pointer mb-2">Stack Trace</summary>{this.state.errorInfo?.componentStack}</details>
        </div>
      );
    }
    return this.props.children;
  }
}

// --- MAIN COMPONENT ---

function EventControlCenterContent({ user, onLogout }) {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const fileInputRef = useRef(null);

  // State
  const [activeTab, setActiveTab] = useState("control");
  const [activeFeature, setActiveFeature] = useState(null); // 'seating_plan', 'dresscode', 'schedule', 'wishlist'
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Cache buster for images
  const [cacheBuster] = useState(Date.now());

  // Hero State
  const [heroImage, setHeroImage] = useState("/images/headers/party_new.png");
  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState("");
  const [tempTitlePositionY, setTempTitlePositionY] = useState(0);
  const [editingDate, setEditingDate] = useState(false);
  const [tempDate, setTempDate] = useState("");
  const [editingLocation, setEditingLocation] = useState(false);
  const [tempLocation, setTempLocation] = useState("");
  const [editingType, setEditingType] = useState(false);
  const [tempType, setTempType] = useState("");
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Toggles State
  const [features, setFeatures] = useState({
    location: true, seating_plan: false, schedule: true, guestbook: true, tasks: true,
    selfie: true, stats: false, photoUpload: true, videoUpload: true, music: true, dish_voting: true
  });
  const [summary, setSummary] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const toggleFeature = (key) => setFeatures(prev => ({ ...prev, [key]: !prev[key] }));

  const isOwner = user?.id && event?.user_id ? user.id === event.user_id : false;

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
  }, [eventId]);

  const startTutorial = () => {
    if (!isOwner) return;
    const driverObj = driver({
      showProgress: true,
      animate: true,
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      steps: [
        {
          element: '.tour-hero-title',
          popover: { title: 'Dein Event', description: 'Klicke auf den Titel, um den Namen deines Events jederzeit anzupassen.', side: "bottom", align: 'center' }
        },
        {
          element: '.tour-hero-date',
          popover: { title: 'Event-Details', description: 'Passe schnell Datum, Ort und Art deines Events an.', side: "top", align: 'center' }
        },
        {
          element: '.tour-invite-btn',
          popover: { title: 'Gäste einladen', description: 'Teile den Link mit deinen Gästen per WhatsApp, E-Mail oder direkt als Link.', side: "bottom", align: 'center' }
        },
        {
          element: '.tour-stats',
          popover: { title: 'Deine Statistiken', description: 'Behalte den Überblick über Zusagen und geteilte Bilder.', side: "bottom", align: 'start' }
        },
        {
          element: '.tour-modules',
          popover: { title: 'Organisiere deine Module', description: 'Aktiviere oder deaktiviere Module wie den Sitzplan, Dresscode oder das Gästebuch nach deinen Wünschen.', side: "top", align: 'start' }
        },
        {
          element: '.tour-community-chat',
          popover: { title: 'Community Chat', description: 'Tritt direkt in Kontakt mit deinen Gästen im Live-Chat für dein Event.', side: "top", align: 'start' }
        }
      ],
      onDestroyed: () => {
        localStorage.setItem('hasSeenEventTutorial', 'true');
      }
    });

    driverObj.drive();
  };

  useEffect(() => {
    if (isOwner && event && !loading) {
      const hasSeenTutorial = localStorage.getItem('hasSeenEventTutorial');
      if (!hasSeenTutorial) {
        setTimeout(() => startTutorial(), 1500);
      }
    }
  }, [isOwner, event, loading]);

  // Live countdown
  useEffect(() => {
    if (event) {
      const updateCountdown = () => {
        try {
          if (!event.date) return;
          const eventDate = parseISO(event.date);
          const now = new Date();
          if (eventDate > now) {
            setCountdown({
              days: Math.max(0, differenceInDays(eventDate, now)),
              hours: Math.max(0, differenceInHours(eventDate, now) % 24),
              minutes: Math.max(0, differenceInMinutes(eventDate, now) % 60),
              seconds: Math.max(0, differenceInSeconds(eventDate, now) % 60)
            });
          }
        } catch (e) { }
      };
      updateCountdown();
      const timer = setInterval(updateCountdown, 1000);
      return () => clearInterval(timer);
    }
  }, [event]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      const eventRes = await authGet(`/events/${eventId}`);
      setEvent(eventRes.data);
      const currentEvent = eventRes.data;

      // Init Hero State
      setTempTitle(currentEvent.title);
      setTempTitlePositionY(currentEvent.title_position_y || 0);
      setTempDate(currentEvent.date ? safelyFormatDate(currentEvent.date, "yyyy-MM-dd'T'HH:mm") : "");
      setTempLocation(currentEvent.location || "");
      setTempType(currentEvent.event_type || "other");

      if (currentEvent.cover_image) {
        let path = currentEvent.cover_image;
        if (path.startsWith('data:')) {
          setHeroImage(path);
        } else {
          if (path.includes('uploads/')) {
            const parts = path.split('uploads/');
            path = `/api/media/${parts[parts.length - 1]}`;
          }
          if (!path.startsWith('/') && !path.startsWith('http')) {
            path = `/${path}`;
          }
          let coverImageUrl = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;

          // Add cache buster
          if (coverImageUrl.includes('/api/media/')) {
            coverImageUrl = `${coverImageUrl}?v=${cacheBuster}`;
          }

          setHeroImage(coverImageUrl);
        }
      } else {
        const typeInfo = EVENT_TYPE_LABELS[currentEvent.event_type?.toLowerCase()] || EVENT_TYPE_LABELS.other;
        setHeroImage(typeInfo?.image || EVENT_TYPE_LABELS.other.image);
      }

      // Fetch Sub-resources
      const [tasksRes, photosRes] = await Promise.all([
        authGet(`/events/${eventId}/tasks`).catch(() => ({ data: [] })),
        authGet(`/events/${eventId}/photos?limit=8`).catch(() => ({ data: [] }))
      ]);
      setTasks((tasksRes.data || []).slice(0, 5));
      setRecentPhotos(photosRes.data || []);

      if (currentEvent.summary) {
        setSummary(currentEvent.summary);
        // Performance Optimization: Lazy load heavy summary image
        if (!currentEvent.summary.image) {
          authGet(`/events/${eventId}/summary`)
            .then(res => {
              if (res.data?.summary?.image) {
                setSummary(prev => ({ ...prev, image: res.data.summary.image }));
              }
            })
            .catch(() => { }); // Silent fail is fine, image just won't show
        }
      }

    } catch (error) {
      console.error("Failed to fetch event data:", error);
      toast.error("Event konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Bild zu groß (Max 10MB)"); return; }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/events/${eventId}/cover`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        }
      });

      const newUrl = res.data.cover_image;
      // Add cache buster immediately
      const displayUrl = newUrl.startsWith('http') ? newUrl : `${BACKEND_URL}${newUrl}?v=${Date.now()}`;

      setHeroImage(displayUrl);
      setEvent(prev => ({ ...prev, cover_image: newUrl }));
      toast.success("Hintergrundbild erfolgreich hochgeladen");
    } catch (e) {
      console.error("Upload failed:", e);
      toast.error("Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleSaveField = async (field, value) => {
    try {
      await authPut(`/events/${eventId}`, { [field]: value });
      setEvent(prev => ({ ...prev, [field]: value }));
      toast.success("Gespeichert");
      if (field === 'date') setEditingDate(false);
      if (field === 'location') setEditingLocation(false);
      if (field === 'event_type') setEditingType(false);
    } catch (e) { toast.error("Speichern fehlgeschlagen"); }
  };

  const handleSaveTitle = async () => {
    try {
      await authPut(`/events/${eventId}`, { title: tempTitle, title_position_y: tempTitlePositionY });
      setEvent(prev => ({ ...prev, title: tempTitle, title_position_y: tempTitlePositionY }));
      setEditingTitle(false);
      toast.success("Titel gespeichert");
    } catch (e) { toast.error("Fehler"); }
  };

  const openGoogleMaps = () => {
    if (event?.location) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`, '_blank');
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/e/${event.share_code}`);
    toast.success("Link kopiert!");
    setShowShareMenu(false);
  };

  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    try {
      const response = await axios.post(`${API}/events/${eventId}/generate-summary`, {}, {
        headers: getAuthHeaders()
      });
      setSummary(response.data.summary);
      setShowSummaryDialog(true);
      toast.success("Zusammenfassung erstellt!");
    } catch (error) {
      console.error(error);
      toast.error("Fehler beim Erstellen der Zusammenfassung");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Lade Event...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mb-6">
          <X className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2">Event nicht gefunden</h2>
        <p className="text-slate-500 mb-8 max-w-xs">Dieses Event existiert nicht oder du hast keine Berechtigung es zu sehen.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-slate-900 text-white px-8 py-3 rounded-full font-bold text-sm uppercase tracking-widest shadow-lg"
        >
          Zum Dashboard
        </button>
      </div>
    );
  }

  // SUBMENU RENDERING logic
  if (activeFeature) {
    let Component = null;
    let title = "";

    switch (activeFeature) {
      case 'tasks': Component = EventTaskList; title = "Aufgabenliste"; break;
      case 'seating_plan': Component = SeatingPlan; title = "Sitzplan"; break;
      case 'dresscode': Component = EventDresscode; title = "Dresscode"; break;
      case 'schedule': Component = EventTimeline; title = "Zeitplan"; break;
      case 'wishlist': Component = EventWishlist; title = "Geschenkwünsche"; break;
      case 'dish_voting': Component = DishVoting; title = "Menü-Planung"; break;
      case 'gallery':
        return (
          <>
            <EventGalleryNew
              eventId={eventId}
              eventTitle={event.title}
              eventDate={event.date}
              isOwner={isOwner}
              onBack={() => setActiveFeature(null)}
            />
            <GuestViewFloatingButton />
          </>
        );
      case 'guests':
        return (
          <FeatureDetailView title="Gästemanagement" onBack={() => setActiveFeature(null)}>
            <div className="bg-white rounded-[3rem] p-6 shadow-sm border border-slate-100 mt-4 mx-4">
              <GuestManagement eventId={eventId} editable={true} />
            </div>
          </FeatureDetailView>
        );
      default: return null;
    }

    return (
      <>
        <FeatureDetailView title={title} onBack={() => setActiveFeature(null)}>
          <Component eventId={eventId} isOwner={isOwner} />
        </FeatureDetailView>
        <GuestViewFloatingButton />
      </>
    );
  }

  const eventType = EVENT_TYPE_LABELS[event.event_type?.toLowerCase()] || EVENT_TYPE_LABELS.other;

  return (
    <div className="min-h-screen bg-slate-50 font-['Plus_Jakarta_Sans'] pb-32">
      <GlobalBackgroundComponent heroImage={heroImage} event={event} setHeroImage={setHeroImage} />
      <GuestViewFloatingButton />

      {/* --- HERO SECTION --- */}
      <section className="relative h-[85vh] flex flex-col justify-between px-6 pt-6 pb-24 md:pb-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={heroImage} className="w-full h-full object-cover" alt="Background" />
          <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-slate-50/90" />
        </div>

        {isOwner && (
          <>
            <input type="file" ref={fileInputRef} onChange={handleImageUpload} className="hidden" accept="image/*" />
            <div className="absolute top-6 right-6 z-50 flex items-center gap-2">
              <button
                onClick={() => { localStorage.removeItem('hasSeenEventTutorial'); startTutorial(); }}
                className="px-3 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all shadow-lg flex items-center gap-2 group border border-white/20"
                title="Tour starten"
              >
                <HelpCircle className="w-4 h-4 text-white" />
                <span className="text-xs font-bold uppercase tracking-wider hidden sm:inline">Tour</span>
              </button>
              <button
                onClick={() => fileInputRef.current.click()}
                className="px-3 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all shadow-lg flex items-center gap-2 group border border-white/20"
              >
                <ImageIcon className="w-4 h-4 group-hover:scale-110 transition-transform text-white" />
                <span className="text-xs font-bold uppercase tracking-wider hidden sm:inline">Hintergrund</span>
              </button>
            </div>
          </>
        )}

        <div 
          className={`relative w-full flex flex-col items-center pt-12 gap-4 transition-transform duration-300 ${editingTitle ? 'z-50' : 'z-10'}`}
          style={{ transform: `translateY(${editingTitle ? tempTitlePositionY : (event?.title_position_y || 0)}px)` }}
        >
          {/* Countdown */}
          <div className="flex gap-4 md:gap-6 mb-0 animate-in fade-in slide-in-from-top-4 duration-700">
            {['Tage', 'Stunden', 'Minuten'].map((label, i) => {
              const value = i === 0 ? countdown.days : i === 1 ? countdown.hours : countdown.minutes;
              return (
                <div key={label} className="flex flex-col items-center">
                  <span className="text-4xl font-serif font-medium text-slate-900 leading-none drop-shadow-sm">
                    {value < 10 ? `0${value}` : value}
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 mt-1">{label}</span>
                </div>
              )
            })}
          </div>

          {/* Title Area */}
          <div className="tour-hero-title relative group mt-4 max-w-4xl mx-auto text-center">
            {editingTitle ? (
              <div className="flex flex-col gap-4 items-center bg-white/60 backdrop-blur-xl p-6 rounded-[2rem] shadow-2xl border border-white/50 w-full max-w-lg">
                <input value={tempTitle} onChange={(e) => setTempTitle(e.target.value)} className="text-4xl md:text-5xl font-medium text-center bg-white/60 backdrop-blur-xl border border-emerald-500 rounded-2xl text-slate-900 p-3 w-full focus:outline-none shadow-inner" style={{ fontFamily: THEME.fontSerif }} autoFocus />
                
                <div className="w-full flex flex-col gap-2 mt-2">
                  <div className="flex justify-between items-center px-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1"><ChevronDown className="w-3 h-3"/> Vertikale Position</span>
                    <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded text-emerald-600 shadow-sm">{tempTitlePositionY}px</span>
                  </div>
                  <input type="range" min="-100" max="400" value={tempTitlePositionY} onChange={(e) => setTempTitlePositionY(Number(e.target.value))} className="w-full accent-emerald-500 cursor-pointer" />
                </div>
                
                <div className="flex gap-3 w-full mt-4">
                  <button onClick={handleSaveTitle} className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white font-bold transition-all flex items-center justify-center gap-2 shadow-sm"><Check className="w-4 h-4" /> Speichern</button>
                  <button onClick={() => { setEditingTitle(false); setTempTitlePositionY(event?.title_position_y || 0); setTempTitle(event?.title || ""); }} className="py-3 px-4 bg-white hover:bg-slate-50 rounded-xl text-slate-500 font-bold transition-all flex items-center justify-center shadow-sm"><X className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <h1 onClick={() => isOwner && setEditingTitle(true)} className="text-5xl md:text-8xl font-medium tracking-tight text-slate-900 cursor-pointer hover:text-emerald-700 transition-colors drop-shadow-sm leading-[0.9]" style={{ fontFamily: THEME.fontSerif }}>{event.title}</h1>
                {isOwner && <button onClick={() => setEditingTitle(true)} className="absolute -top-4 -right-8 p-2 bg-white/50 hover:bg-white rounded-full text-slate-400 hover:text-emerald-600 transition-all opacity-0 group-hover:opacity-100"><Edit className="w-3 h-3" /></button>}
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10 w-full flex flex-col items-center pb-0">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 1 }} className="flex flex-col items-center gap-1 mb-8">
            <span className="text-[8px] font-black uppercase tracking-[0.4em] text-slate-500">Scroll Down</span>
            <ChevronDown className="w-6 h-6 text-slate-900 animate-bounce" />
          </motion.div>

          <div className="tour-hero-date grid grid-cols-2 md:grid-cols-3 gap-2 w-full max-w-[550px] px-2 mb-4">
            {/* Date */}
            <div className="bg-white/80 backdrop-blur-md border border-white/50 p-2 rounded-2xl shadow-sm flex flex-col items-center justify-center gap-0.5 relative group h-16">
              {editingDate ? (
                <div className="flex items-center gap-1 w-full px-1">
                  <input type="datetime-local" value={tempDate} onChange={(e) => setTempDate(e.target.value)} className="w-full text-[10px] bg-white border rounded" />
                  <button onClick={() => handleSaveField('date', tempDate)} className="text-emerald-500"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingDate(false)} className="text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <span className="text-[7px] font-black text-emerald-600 uppercase tracking-[0.3em]">Datum</span>
                  <p className="text-lg font-black text-slate-900 uppercase leading-none">{safelyFormatDate(event.date, "dd. MMM")}</p>
                  <p className="text-[9px] font-bold text-slate-500">{safelyFormatDate(event.date, "HH:mm")} Uhr</p>
                  {isOwner && <button onClick={() => { setTempDate(event.date ? safelyFormatDate(event.date, "yyyy-MM-dd'T'HH:mm") : ""); setEditingDate(true); }} className="absolute top-1 right-1 p-1 bg-white rounded-full opacity-0 group-hover:opacity-100"><Edit className="w-3 h-3 text-slate-400" /></button>}
                </>
              )}
            </div>

            {/* Location */}
            <div className="bg-slate-900/90 backdrop-blur-md border border-white/10 p-2 rounded-2xl shadow-sm flex flex-col items-center justify-center gap-0.5 relative group h-16 cursor-pointer" onClick={() => !editingLocation && openGoogleMaps()}>
              {editingLocation ? (
                <div className="flex items-center gap-1 w-full px-1">
                  <input value={tempLocation} onChange={(e) => setTempLocation(e.target.value)} className="w-full text-[10px] bg-white/10 text-white border-none rounded" />
                  <button onClick={() => handleSaveField('location', tempLocation)} className="text-emerald-500"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingLocation(false)} className="text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <span className="text-[7px] font-black text-emerald-400 uppercase tracking-[0.3em]">Location</span>
                  <div className="flex items-center gap-1.5 text-white">
                    <MapPin className="w-3 h-3 text-emerald-400" />
                    <p className="text-[10px] font-black uppercase truncate max-w-[100px]">{event.location?.split(',')[0] || 'Berlin'}</p>
                  </div>
                  {isOwner && <button onClick={(e) => { e.stopPropagation(); setTempLocation(event.location || ""); setEditingLocation(true); }} className="absolute top-1 right-1 p-1 bg-white/10 rounded-full opacity-0 group-hover:opacity-100"><Edit className="w-3 h-3 text-white" /></button>}
                </>
              )}
            </div>

            {/* Type */}
            <div className="bg-slate-900/90 backdrop-blur-md border border-white/10 p-2 rounded-2xl shadow-sm flex flex-col items-center justify-center gap-0.5 relative group h-16">
              {editingType ? (
                <div className="flex items-center gap-1 w-full px-1">
                  <select value={tempType} onChange={(e) => setTempType(e.target.value)} className="w-full text-[10px] bg-white/10 text-white border-none rounded appearance-none"><option value="other">...</option>{Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k} className="text-black">{v.label}</option>)}</select>
                  <button onClick={() => handleSaveField('event_type', tempType)} className="text-emerald-500"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingType(false)} className="text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <span className="text-[7px] font-black text-emerald-400 uppercase tracking-[0.3em]">Event Typ</span>
                  <div className="flex items-center gap-1.5 text-white">
                    <Wand2 className="w-3 h-3 text-emerald-400" />
                    <p className="text-[10px] font-black uppercase truncate max-w-[100px]">{eventType.label}</p>
                  </div>
                  {isOwner && <button onClick={() => { setTempType(event.event_type || "other"); setEditingType(true); }} className="absolute top-1 right-1 p-1 bg-white/10 rounded-full opacity-0 group-hover:opacity-100"><Edit className="w-3 h-3 text-white" /></button>}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* --- DASHBOARD CONTENT --- */}
      <div className="relative z-10 -mt-10 px-6 space-y-8 rounded-t-[3rem] bg-slate-50 pt-10">

        <div className="text-center mb-8">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1">CONTROL CENTER</p>
          <div className="flex justify-center gap-3 mt-4">
            <button onClick={() => setShowShareMenu(true)} className="tour-invite-btn bg-[#10B981] hover:bg-[#059669] text-white px-8 py-3 rounded-full font-bold text-xs uppercase tracking-widest shadow-lg shadow-emerald-200 flex items-center gap-2 transition-all active:scale-95">
              <Share2 className="w-4 h-4" /> EINLADEN
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="tour-stats grid grid-cols-2 gap-4">
          <div onClick={() => setActiveFeature('guests')} className="bg-white rounded-[2rem] p-5 flex items-center gap-4 shadow-sm border border-slate-100 cursor-pointer hover:scale-[1.02] transition-transform">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 shrink-0">
              <UsersIcon className="w-6 h-6" />
            </div>
            <div>
              <span className="block text-2xl font-black text-slate-900 leading-none">
                {event?.guest_stats?.confirmed || 0}
              </span>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                von {event?.guest_count || 0} Gästen
              </span>
            </div>
          </div>
          <div onClick={() => setActiveFeature('gallery')} className="bg-white rounded-[2rem] p-5 flex items-center gap-4 shadow-sm border border-slate-100 cursor-pointer hover:scale-[1.02] transition-transform">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shrink-0">
              <ImageIcon className="w-6 h-6" />
            </div>
            <div>
              <span className="block text-2xl font-black text-slate-900 leading-none">{recentPhotos.length}</span>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Medien</span>
            </div>
          </div>
        </div>



        {/* Sections */}
        <div className="space-y-6">
          {/* Organisieren */}
          <div className="tour-modules">
            <SectionCard title="Organisieren" icon={ClipboardList} color="emerald">
              <FeatureRow icon={MapPin} label="Standort / Location" subLabel={event?.location || "Nicht festgelegt"} checked={features.location} onChange={() => toggleFeature('location')} color="emerald" />

              <FeatureRow
                icon={UsersIcon}
                label="Sitzplan"
                subLabel="Sichtbar für Gäste"
                onClick={() => setActiveFeature('seating_plan')}
                color="emerald"
                action={
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); setActiveFeature('seating_plan'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md min-w-[100px] transition-all active:scale-95">
                      Planen
                    </button>
                    <ToggleSwitch checked={features.seating_plan} onCheckedChange={() => toggleFeature('seating_plan')} color="emerald" />
                  </div>
                }
              />

              <FeatureRow
                icon={User}
                label="Dresscode"
                subLabel="Stil festlegen"
                onClick={() => setActiveFeature('dresscode')}
                color="emerald"
                action={
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); setActiveFeature('dresscode'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md min-w-[100px] transition-all active:scale-95">
                      Wählen
                    </button>
                    <div className="w-12 h-7" />
                  </div>
                }
              />

              <FeatureRow
                icon={Clock}
                label="Zeitplan"
                subLabel="Ablauf freigeben"
                onClick={() => setActiveFeature('schedule')}
                color="emerald"
                action={
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); setActiveFeature('schedule'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md min-w-[100px] transition-all active:scale-95">
                      Ablauf
                    </button>
                    <ToggleSwitch checked={features.schedule} onCheckedChange={() => toggleFeature('schedule')} color="emerald" />
                  </div>
                }
              />

              <FeatureRow
                icon={ClipboardList}
                label="Aufgabenliste"
                subLabel="Tasks verwalten"
                onClick={() => setActiveFeature('tasks')}
                color="emerald"
                action={
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); setActiveFeature('tasks'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md min-w-[100px] transition-all active:scale-95">
                      Planen
                    </button>
                    <ToggleSwitch checked={features.tasks} onCheckedChange={() => toggleFeature('tasks')} color="emerald" />
                  </div>
                }
              />

              <FeatureRow
                icon={Wand2}
                label="Geschenkwünsche"
                subLabel="Liste verwalten"
                onClick={() => setActiveFeature('wishlist')}
                color="emerald"
                action={
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); setActiveFeature('wishlist'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md min-w-[100px] transition-all active:scale-95">
                      Wünsche
                    </button>
                    <div className="w-12 h-7" />
                  </div>
                }
              />
            </SectionCard>
          </div>

          {/* Entertainment */}
          <SectionCard title="Entertainment" icon={Wand2} color="pink">
            <FeatureRow icon={ClipboardList} label="Gästebuch" subLabel="Moderation aktiv" checked={features.guestbook} onChange={() => toggleFeature('guestbook')} color="pink" />
            <FeatureRow icon={Camera} label="Selfie Spiel" subLabel="Interaktive Aufgaben" checked={features.selfie} onChange={() => toggleFeature('selfie')} color="pink" />
            <FeatureRow
              icon={UtensilsCrossed}
              label="Gericht-Voting"
              subLabel="Über Gerichte abstimmen"
              onClick={() => setActiveFeature('dish_voting')}
              color="pink"
              action={
                <div className="flex items-center gap-3">
                  <button onClick={(e) => { e.stopPropagation(); setActiveFeature('dish_voting'); }} className="bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md min-w-[100px] transition-all active:scale-95">
                    Planen
                  </button>
                  <ToggleSwitch checked={features.dish_voting} onCheckedChange={() => toggleFeature('dish_voting')} color="pink" />
                </div>
              }
            />
            <FeatureRow icon={Trophy} label="Rangliste / Stats" subLabel="Bestenliste anzeigen" checked={features.stats} onChange={() => toggleFeature('stats')} color="pink" />
          </SectionCard>

          {/* Medien Pool */}
          <SectionCard title="Medien Pool" icon={ImageIcon} color="blue">
            <FeatureRow icon={ImageIcon} label="Foto Upload" checked={features.photoUpload} onChange={() => toggleFeature('photoUpload')} color="blue" />
            <FeatureRow icon={Activity} label="Video Upload" checked={features.videoUpload} onChange={() => toggleFeature('videoUpload')} color="blue" />
            <FeatureRow icon={Music} label="Musik Wünsche" checked={features.music} onChange={() => toggleFeature('music')} color="blue" />
          </SectionCard>
        </div>

        {/* Community Chat Link */}
        <div onClick={() => event && navigate(`/event/${event.id}/chat`)} className="tour-community-chat bg-[#0F172A] rounded-[2.5rem] p-8 relative overflow-hidden cursor-pointer group shadow-xl shadow-slate-200">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center border border-emerald-500/20">
                <MessageCircle className="w-7 h-7 text-emerald-500" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white">Community<br />Chat</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Gäste Live Support</p>
              </div>
            </div>
            <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
              <ChevronRight className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* --- RECAP SECTION --- */}
        <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4">
            <Sparkles className="w-8 h-8 text-yellow-400 opacity-20 animate-pulse" />
          </div>
          <div className="relative z-10">
            <h3 className="text-xl font-black text-slate-900 mb-2">Event Rückblick</h3>
            <p className="text-sm text-slate-500 mb-6">Erstelle eine magische KI-Collage deiner besten Momente und teile sie mit deinen Gästen.</p>

            {summary ? (
              <div className="space-y-4">
                <div className="aspect-[4/5] rounded-2xl bg-slate-50 border border-slate-100 overflow-hidden relative group">
                  <img src={summary.image} alt="Recap" className="w-full h-full object-contain" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                    <button onClick={() => setShowSummaryDialog(true)} className="bg-white text-slate-900 p-3 rounded-full shadow-xl"><ImageIcon className="w-6 h-6" /></button>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowSummaryDialog(true)} className="flex-1 bg-slate-900 text-white font-bold py-4 rounded-2xl text-xs uppercase tracking-widest shadow-lg">Ansehen</button>
                  <button onClick={() => {
                    if (window.confirm("Neu generieren überschreibt die aktuelle Collage. Fortfahren?")) handleGenerateSummary();
                  }} className="px-6 bg-slate-100 text-slate-500 py-4 rounded-2xl transition-colors hover:bg-slate-200">
                    <Clock className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary}
                className="w-full bg-gradient-to-r from-indigo-600 to-emerald-600 text-white font-black py-5 rounded-[1.5rem] text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                {isGeneratingSummary ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    KI generiert...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Highlights generieren
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Share Dialog */}
      <Dialog open={showShareMenu} onOpenChange={setShowShareMenu}>
        <DialogContent className="max-w-xl bg-slate-900 border-white/10 text-white rounded-[2rem] p-8 font-sans">
          <DialogTitle className="text-2xl font-bold text-center mb-8">Einladen</DialogTitle>
          <div className="grid grid-cols-3 gap-6">
            <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out my event: ${window.location.origin}/e/${event.share_code}`)}`, '_blank')} className="flex flex-col items-center gap-2 group">
              <div className="w-16 h-16 bg-[#25D366] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><MessageCircle className="w-8 h-8 text-white fill-white" /></div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-white/50">WhatsApp</span>
            </button>
            <button onClick={() => window.location.href = `mailto:?body=${encodeURIComponent(window.location.origin + '/e/' + event.share_code)}`} className="flex flex-col items-center gap-2 group">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10 shadow-lg group-hover:scale-110 transition-transform"><Mail className="w-8 h-8 text-white" /></div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-white/50">Email</span>
            </button>
            <button onClick={copyShareLink} className="flex flex-col items-center gap-2 group">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10 shadow-lg group-hover:scale-110 transition-transform"><Copy className="w-8 h-8 text-white" /></div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-white/50">Link</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Dialog - Now Rich Experience */}
      <Dialog open={showSummaryDialog} onOpenChange={setShowSummaryDialog}>
        <DialogContent className="max-w-[100vw] sm:max-w-2xl bg-[#fdfbf7] p-0 overflow-hidden h-[100vh] sm:h-[90vh] flex flex-col border-none">
          <div className="flex-1 overflow-y-auto custom-scrollbar relative">
            <div className="absolute top-6 right-6 z-[60]">
              <button
                onClick={() => setShowSummaryDialog(false)}
                className="bg-white/80 hover:bg-white text-slate-900 rounded-full p-2 backdrop-blur-md transition-all shadow-xl border border-slate-100"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <EventRecapView
              summary={summary}
              event={event}
              onDownload={() => {
                const link = document.createElement("a");
                link.href = summary.image;
                link.download = `Event_Recap_${event.title}.jpg`;
                link.click();
              }}
              onShare={() => {
                if (navigator.share) {
                  navigator.share({
                    title: `Recap: ${event.title}`,
                    text: summary.text,
                    url: `${window.location.origin}/e/${event.share_code}`
                  }).catch(console.error);
                } else {
                  toast.info("Teilen nicht unterstützt");
                }
              }}
            />

            {event && summary && (
              <div className="mt-8 mb-24 px-6 text-center">
                <button
                  onClick={() => {
                    if (window.confirm("Möchtest du einen neuen KI-Rückblick generieren?")) {
                      setShowSummaryDialog(false);
                      handleGenerateSummary();
                    }
                  }}
                  className="text-[10px] text-slate-400 font-bold uppercase tracking-widest hover:text-amber-600 transition-colors underline"
                >
                  Neu generieren (AI-Magie erzwingen)
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex items-center justify-around py-4 max-w-md mx-auto">
          <button onClick={() => navigate("/dashboard")} className="flex flex-col items-center gap-1.5 text-gray-400 hover:text-emerald-600 hover:fill-emerald-600 transition-colors">
            <Home className="w-5 h-5" />
            <span className="text-[9px] font-black uppercase tracking-widest">Home</span>
          </button>
          <button onClick={() => navigate("/create-event")} className="flex flex-col items-center gap-1.5 text-gray-400 hover:text-slate-900">
            <Plus className="w-5 h-5" />
            <span className="text-[9px] font-black uppercase tracking-widest">Neu</span>
          </button>
          <button onClick={() => event && navigate(`/event/${event.id}/chat`)} className="flex flex-col items-center gap-1.5 text-gray-400 hover:text-slate-900">
            <MessageCircle className="w-5 h-5" />
            <span className="text-[9px] font-black uppercase tracking-widest">Chat</span>
          </button>
          <button onClick={() => navigate("/profile")} className="flex flex-col items-center gap-1.5 text-gray-400 hover:text-slate-900">
            <ProfileIcon className="w-5 h-5" />
            <span className="text-[9px] font-black uppercase tracking-widest">Profil</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventControlCenter(props) {
  return (
    <ErrorBoundary>
      <EventControlCenterContent {...props} />
    </ErrorBoundary>
  );
}
